import React, {useEffect, useState, useRef} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {size, isEmpty} from 'lodash';

import {labels} from '../../../../../../util/utils';
import {GARoleTypeId, projectSessionKeys} from '../../../../../../util/uiconstants';
import Utility from '../../../../../../util/utilityfunctions';

import BadgeLabel from '../../../../../Common/Badge/BadgeLabel';
import ExportToExcel from '../../../../../Common/ExportToExcel/ExportToExcel';
import {loadStorage, saveStorage} from '../../../../../Common/localStorage';
import FilterChips from '../../../../../Common/FilterChips';

import MetricsTable from '../../../../../Common/MetricsTable';
import Select from '../../../../../CoreWrappers/Select';

const GroupAuditEngagementTable = ({engagementMetrics}) => {
	const dashboardContext = useSelector(({dashboardContext}) => dashboardContext);
	const isGroupAuditComponentTileEnabled = useSelector(
		({permissions}) => permissions?.configurationSettings?.features?.groupAuditComponentStatusTileEnable
	);
	const dashboardGroupAuditFilters = useSelector(({dashboardFilters}) => dashboardFilters.groupAudit);

	const filterId = {
		primary: 1,
		component: 2
	};

	const [groupAuditValue, setgroupAuditValue] = useState(null);
	const [selectedFilter, setSelectedFilter] = useState(filterId.primary);
	const [engagementData, setEngagementData] = useState(null);
	const [showMessage, setShowMessage] = useState(false);
	const [userChange, setUserChange] = useState(false);

	const hasFiltersApplyed = dashboardGroupAuditFilters?.filters?.length || false;
	const chipFilters = dashboardGroupAuditFilters?.filters;

	const filters = [
		{id: filterId.primary, label: labels.primary},
		{id: filterId.component, label: labels.component, disabled: !isGroupAuditComponentTileEnabled}
	];

	const milestonesOptions = Utility.getMilestoneOptionWithGroup();

	const projectId = Utility.getProjectIdFromUrl();
	const psessionKEy = `${projectSessionKeys.projectID}_${projectId}_${projectSessionKeys.groupAuditSession}`;

	useEffect(() => {
		const currentState = loadStorage(psessionKEy);
		if (currentState && currentState.groupFilterid) {
			setSelectedFilter(currentState.groupFilterid);
		}
	}, []);

	useEffect(() => {
		if (!userChange) {
			// Use engagement data.
			setgroupAuditValue(milestonesOptions[0].options[0]);
		}
	}, [engagementData]);

	useEffect(() => {
		if (groupAuditValue?.id) {
			buildTable();
		}
	}, [engagementMetrics, groupAuditValue, selectedFilter, chipFilters]);

	useEffect(() => {
		if (hasFiltersApplyed) {
			const isComponentTaskStatusFilterApplied = chipFilters?.some((chip) =>
				[labels.componentTask].includes(chip.categoryLabel)
			);
			const selectedTable = isComponentTaskStatusFilterApplied ? filterId.component : filterId.primary;
			setSelectedFilter(selectedTable);
		}
	}, [dashboardGroupAuditFilters]);

	const getExportDetails = () => {
		return {
			title: filters.find((f) => f.id === selectedFilter).label,
			columns: getColumnsToExport(),
			data: getDataToExport(),
			worksheetName: filters.find((f) => f.id === selectedFilter).label
		};
	};

	const getColumnsToExport = () => {
		let columns = [];
		columns.push({header: labels.orgChartEntity, key: 'entityName'});
		columns.push({header: labels.engagementId, key: 'engId'});
		columns.push({header: labels.contactName, key: 'ContactName'});
		columns.push({header: labels.contactEmail, key: 'ContactEmail'});
		columns.push({header: labels.engagementLabel, key: 'engDes'});
		if (selectedFilter === filterId.component) {
			columns.push({header: labels.totalTasksCapitalized, key: 'totalTasksCapitalized'});
			columns.push({header: labels.groupAuditNotSent, key: 'groupAuditNotSent'});
			columns.push({header: labels.statusNotSent, key: 'statusNotSent'});
			columns.push({header: labels.statusReturned, key: 'statusReturned'});
			columns.push({header: labels.statusDocumentationPending, key: 'statusDocumentationPending'});
			columns.push({header: labels.statusDocumentationComplete, key: 'statusDocumentationComplete'});
			columns.push({header: labels.groupAuditUnlinked, key: 'groupAuditUnlinked'});
		} else {
			columns.push({header: labels.groupAuditTotalForms, key: 'groupAuditTotalForms'});
			columns.push({header: labels.statusOpen, key: 'statusOpen'});
			columns.push({header: labels.statusInProgress, key: 'statusInProgress'});
			columns.push({header: labels.statusInReview, key: 'statusInReview'});
			columns.push({header: labels.statusComplete, key: 'statusComplete'});
			columns.push({header: labels.overdueLabel, key: 'overdueLabel'});
			columns.push({header: labels.returned, key: 'returned'});
		}
		return columns;
	};
	const getDataToExport = () => {
		if (selectedFilter === filterId.component) {
			return createComponentDataToExport();
		} else {
			return createPrimaryDataToExport();
		}
	};

	const createComponentDataToExport = () => {
		let data = [];
		dashboardContext.orgChart.forEach(() => {
			if (dashboardContext && size(dashboardContext.entities) > 0) {
				dashboardContext.entities.forEach((entity) => {
					if (entity.engagements && size(entity.engagements) > 0) {
						entity.engagements.forEach((eng) => {
							let engMetricsData = engagementMetrics?.find((x) => x.engId === eng);
							if (engMetricsData) {
								data.push({
									entityName: entity.label,
									engId: eng,
									ContactName: '',
									ContactEmail: '',
									engDes: engMetricsData.engDes,
									totalTasksCapitalized: engMetricsData.cTTotal,
									groupAuditNotSent: engMetricsData.cTNotSent,
									statusNotSent: engMetricsData.cTNotSent,
									statusReturned: engMetricsData.cTRetrn,
									statusDocumentationPending: engMetricsData.cTPartialSend,
									statusDocumentationComplete: engMetricsData.cTComplSend,
									groupAuditUnlinked: engMetricsData.cTUnlinkTot
								});
							}
						});
					}
				});
			}
		});
		return data;
	};

	const createPrimaryDataToExport = () => {
		let data = [];
		dashboardContext.orgChart.forEach(() => {
			if (dashboardContext && size(dashboardContext.entities) > 0) {
				dashboardContext.entities.forEach((entity) => {
					if (entity.engagements && size(entity.engagements) > 0) {
						entity.engagements.forEach((eng) => {
							let engMetricsData = engagementMetrics?.find((x) => x.engId === eng);
							if (engMetricsData) {
								data.push({
									entityName: entity.label,
									engId: eng,
									ContactName: '',
									ContactEmail: '',
									engDes: engMetricsData.engDes,
									groupAuditTotalForms: engMetricsData.pTTotal,
									statusOpen: engMetricsData.pTOpen,
									statusInProgress: engMetricsData.pTInProg,
									statusInReview: engMetricsData.pTInRev,
									statusComplete: engMetricsData.pTCompl,
									overdueLabel: engMetricsData.pTOD,
									returned: engMetricsData.pTRetrn
								});
							}
						});
					}
				});
			}
		});
		return data;
	};

	const openGroups = useRef([]);

	const loadNodes = (node, entities, data) => {
		// check if entity is selected
		const entity = entities.find((x) => x.id === node.id);

		if (entity?.isEnabled && entity.engagements?.length > 0) {
			const engagements = getEngagements(entity);

			if (!isEmpty(engagements)) {
				const group = {
					id: entity.id,
					name: entity.label,
					isOpen: false,
					items: engagements,
					engCount: engagements.length,
					customGroupRender: () => {
						const result = <BadgeLabel label={entity.label} value={group.engCount} />;
						return result;
					}
				};

				data.push(group);
			}
		}

		// load children entities for each node
		node.nodes.forEach((n) => {
			loadNodes(n, entities, data);
		});
	};

	const getEngagements = (entity) => {
		let engagements = entity.engagements.reduce((filtered, engagementId) => {
			if (!isEmpty(engagementMetrics)) {
				const engagement = {...engagementMetrics.find((x) => x.engId === engagementId)};
				engagement.tableClass = getTableClass();

				if (engagement?.gAType && engagement?.gAType !== 0) {
					// check whether the engagement is region/component or not.
					let isRegionComponent =
						engagement?.gAType === GARoleTypeId.Component || engagement?.gAType === GARoleTypeId.Region;
					let isRegionalPrimary =
						engagement?.gAType === GARoleTypeId.Primary || engagement?.gAType === GARoleTypeId.Region;

					// checks whether the engagement has the expected type.
					let hasExpectedGaType =
						(isRegionComponent && selectedFilter === filterId.component) ||
						(isRegionalPrimary && selectedFilter === filterId.primary);

					// checks whether the engagement satisfies the current search criteria.
					if (hasExpectedGaType) {
						filtered.push(engagement);
					}
				}
			}

			let areEngagements = filtered.filter((e) => e !== undefined);

			if (hasFiltersApplyed) {
				areEngagements = areEngagements.filter((eng) => dashboardGroupAuditFilters?.engagements.includes(eng.engId));
			}
			if (_.isEmpty(areEngagements)) {
				return [];
			} else {
				return areEngagements;
			}
		}, []);

		return engagements;
	};

	const buildTable = () => {
		// check if selected entities have engagements
		if (dashboardContext.entities?.every((entity) => entity.engagements?.length === 0)) {
			setShowMessage(true);
		} else {
			let data = [];

			// show entities base on org chart order and if it was selected
			dashboardContext.orgChart.forEach((node) => {
				loadNodes(node, dashboardContext.entities, data);
			});

			setShowMessage(false);

			// when searching, remove entity without engagement
			if (hasFiltersApplyed) {
				data = data?.filter((x) => x?.items?.length !== 0);
			}

			setEngagementData(data);
		}
	};

	const getTableClass = () => {
		return selectedFilter === filterId.primary ? 'primary-table' : 'component-table';
	};

	const handlerOnSwitchChange = (id) => {
		setSelectedFilter(Number.parseInt(id));
		setUserChange(true);
		buildTable();

		let currentState = loadStorage(psessionKEy);
		if (!currentState) {
			currentState = {};
		}
		currentState.groupFilterid = Number.parseInt(id);
		saveStorage(psessionKEy, currentState);
	};

	const handlerOnGroupChange = (group, isOpen) => {
		setUserChange(true);
		if (isOpen) {
			openGroups.current.push(group.id);
		} else {
			openGroups.current = openGroups.current.filter((x) => x !== group.id);
		}
	};

	const renderCustomDetails = () => {
		if (hasFiltersApplyed) {
			const filterChipsProps = {
				filters: [...chipFilters],
				onClearFilter: dashboardGroupAuditFilters?.onClearFilter
			};

			return <FilterChips {...filterChipsProps} />;
		}
	};

	const renderTableSelect = () => {
		const props = {
			value: selectedFilter,
			wrapperClass: 'table-select',
			options: filters,
			onChange: handlerOnSwitchChange
		};

		return <Select {...props} />;
	};

	const renderTable = () => {
		const tableProps = {
			data: engagementData,
			variant: selectedFilter === filterId.primary ? 'primary' : 'component',
			renderHeader: () => (
				<>
					{renderTableSelect()}
					<ExportToExcel getDataToExport={getExportDetails} className="export-data" />
				</>
			),
			renderFilter: renderCustomDetails
		};

		return <MetricsTable {...tableProps} />;
	};

	return renderTable();
};

GroupAuditEngagementTable.defaultProps = {
	engagementMetrics: []
};

GroupAuditEngagementTable.propTypes = {
	engagementMetrics: PropTypes.arrayOf(
		PropTypes.shape({
			cRAccepted: PropTypes.number,
			cRDue3: PropTypes.number,
			cRDue5: PropTypes.number,
			cRDue10: PropTypes.number,
			cRDue30: PropTypes.number,
			cRDueTom: PropTypes.number,
			cRNotSent: PropTypes.number,
			cROD: PropTypes.number,
			cRPendResp: PropTypes.number,
			cRReceived: PropTypes.number,
			cRRetrn: PropTypes.number,
			cRTotal: PropTypes.number,
			cTComplSend: PropTypes.number,
			cTNotSent: PropTypes.number,
			cTOD: PropTypes.number,
			cTPartialSend: PropTypes.number,
			cTRetrn: PropTypes.number,
			cTTotal: PropTypes.number,
			cTUnlinkTot: PropTypes.number,
			conTaskClo: PropTypes.number,
			conTaskInProg: PropTypes.number,
			conTaskInRev: PropTypes.number,
			conTaskOpen: PropTypes.number,
			conTaskTotal: PropTypes.number,
			conTested: PropTypes.number,
			conTotal: PropTypes.number,
			defDesign: PropTypes.number,
			defOper: PropTypes.number,
			defOperAndDes: PropTypes.number,
			engDes: PropTypes.string,
			engId: PropTypes.number,
			engKey: PropTypes.number,
			gAType: PropTypes.number,
			hasAPEQRSO: PropTypes.bool,
			hasAPPICSO: PropTypes.bool,
			hasCRAEQRSO: PropTypes.bool,
			hasCRAPICSO: PropTypes.bool,
			isEQRCRASODlyd: PropTypes.bool,
			isEQREng: PropTypes.bool,
			isEQRInv: PropTypes.bool,
			isPartAPSODlyd: PropTypes.bool,
			isPartCRASODlyd: PropTypes.bool,
			isPartInvDlyd: PropTypes.bool
		})
	)
};

export default GroupAuditEngagementTable;
